/**
 * Общие функции для текстовой/визуальной истории раздач Секи (локальная игра, мобильный режим).
 */

/** Одна строка в начале текстового экспорта (анализаторы, LLM). */
export const SEKA_HISTORY_EXPORT_HINT_LINE =
  'Подсказки для разбора: «сравнение комбинаций» — карты реально сравнивались (в журнале ходов «Вскрыть» может не быть). У рейза «линия стола» и сумма «со стека в банк» различаются (в т.ч. темнота). «Ваш итог» — дельта стека за руку (анте + все взносы уже учтены).';

/** Карта движка «6H» → формат PlayingCard. */
export function sekaCardToPlaying(cardStr) {
  if (!cardStr || cardStr.length < 2) return null;
  const suitMap = { H: 'hearts', D: 'diamonds', C: 'clubs', S: 'spades' };
  const suit = suitMap[cardStr[1]];
  if (!suit) return null;
  const rank = cardStr[0] === 'T' ? '10' : cardStr[0];
  return { suit, rank };
}

export function formatHistoryAction(actionItem) {
  const action = String(actionItem?.action || '').toUpperCase();
  const paid = Number(actionItem?.paid);
  const matched = Number(actionItem?.matched);
  const usedDarkDiscount =
    Number.isFinite(paid) && Number.isFinite(matched) && paid > 0 && matched > paid;
  if (action === 'CHECK') return 'Чек';
  if (action === 'LOOK') return 'Посмотрел';
  if (action === 'FOLD') return 'Пас';
  if (action === 'CALL') {
    const paidPart =
      Number.isFinite(paid) && paid > 0
        ? `со стека в банк +${paid.toLocaleString('ru-RU')} SEKA${usedDarkDiscount ? ' (в тёмную)' : ''}`
        : null;
    const matchedPart =
      Number.isFinite(matched) && matched > 0
        ? `закрыт уровень ${matched.toLocaleString('ru-RU')} SEKA по линии стола`
        : null;
    if (paidPart && matchedPart) return `Колл: ${matchedPart}, ${paidPart}`;
    if (paidPart) return `Колл: ${paidPart}`;
    return 'Колл';
  }
  if (action === 'RAISE') {
    const target = Number(actionItem?.targetBet || 0);
    const targetPart =
      target > 0 ? `линия стола ${target.toLocaleString('ru-RU')} SEKA` : null;
    const paidPart =
      Number.isFinite(paid) && paid > 0
        ? `со стека в банк +${paid.toLocaleString('ru-RU')} SEKA${usedDarkDiscount ? ' (в тёмную)' : ''} (факт списания; линия — уровень ставки)`
        : null;
    if (targetPart && paidPart) return `Повысил: ${targetPart}, ${paidPart}`;
    if (targetPart) return `Повысил: ${targetPart}`;
    return 'Повысил';
  }
  if (action === 'SHOWDOWN') return 'Вскрытие';
  if (action === 'AUTO_SHOWDOWN_ALLIN_ANTE') return 'Авто-вскрытие (all-in анте)';
  return action || 'Ход';
}

/**
 * Человекочитаемый исход раздачи для экспорта и UI.
 * «Линия стола» и сравнение рук разделены: раньше «без вскрытия» путали с отсутствием сравнения карт.
 *
 * @param {object|null|undefined} hist — запись gameHistory (локально или с сервера).
 * @param {Array|null|undefined} [actionsOverride] — если уже извлечён список ходов.
 * @returns {string}
 */
export function describeSekaHandEndType(hist, actionsOverride = null) {
  const actions = Array.isArray(actionsOverride)
    ? actionsOverride
    : Array.isArray(hist?.actions)
      ? hist.actions
      : [];
  const hasAutoAllInAnte =
    Boolean(hist?.autoShowdownAllInAnte) ||
    actions.some((a) => String(a?.action || '').toUpperCase() === 'AUTO_SHOWDOWN_ALLIN_ANTE');
  if (hasAutoAllInAnte) {
    return 'авто-вскрытие (all-in анте)';
  }
  if (actions.length === 0) {
    return 'авто-завершение (ходы не записаны)';
  }

  const hasShowdownAction = actions.some((a) => String(a?.action || '').toUpperCase() === 'SHOWDOWN');
  const handsLen = Array.isArray(hist?.hands) ? hist.hands.length : 0;

  let comparedCount = handsLen;
  if (comparedCount === 0 && Array.isArray(hist?.players)) {
    comparedCount = hist.players.filter(
      (p) => !Boolean(p?.isFolded) && Array.isArray(p?.cards) && p.cards.length > 0
    ).length;
  }

  if (comparedCount >= 2) {
    return hasShowdownAction
      ? 'сравнение комбинаций (в журнале есть ход «Вскрыть»)'
      : 'сравнение комбинаций (лимит кругов торговли / авто-вскрытие движком)';
  }
  if (comparedCount === 1) {
    return 'победа без сравнения карт (в активном розыгрыше один игрок)';
  }
  if (hasShowdownAction) {
    return 'вскрытие по ходу игрока';
  }
  return 'завершение раздачи (см. ходы и дельты; снимок карт может быть неполным)';
}

/**
 * Одна раздача в текстовый дамп (локальная история + онлайн gameHistory).
 *
 * @param {string[]} lines
 * @param {{
 *   handIndex?: number|string,
 *   handSeq?: number|string,
 *   winnerNames?: string,
 *   pot?: number,
 *   timestamp?: Date|number|null,
 *   heroDelta?: number|null,
 *   endType?: string,
 *   playerSnapshots?: Array,
 *   actions?: Array,
 *   deltas?: Array<{ playerName?: string, delta?: number }>,
 * }} row
 * @param {{
 *   handLabelStyle?: 'hash' | 'numero',
 *   heroDeltaLine?: boolean,
 *   stackDeltaTitle?: string,
 * }} [opts]
 */
export function appendSekaMatchHistoryExportRow(lines, row, opts = {}) {
  if (!row) return;
  const labelStyle = opts.handLabelStyle === 'numero' ? 'numero' : 'hash';
  const hn = row.handIndex ?? row.handSeq;
  const handNum = hn != null && hn !== '' ? hn : '—';
  const handLead =
    labelStyle === 'numero' ? `Раздача №${handNum}` : `Раздача #${handNum}`;
  lines.push(
    `${handLead} · банк ${Number(row.pot || 0).toLocaleString('ru-RU')} SEKA · ${String(row.endType || '—')}`
  );
  lines.push(`  Победитель: ${row.winnerNames || '—'}`);
  if (opts.heroDeltaLine !== false && row.heroDelta != null && Number.isFinite(Number(row.heroDelta))) {
    const hd = Number(row.heroDelta);
    lines.push(`  Ваш итог: ${hd > 0 ? '+' : ''}${hd.toLocaleString('ru-RU')} SEKA`);
    lines.push('  (дельта стека за раздачу: анте и все взносы уже внутри расчёта)');
  }
  const ts = row.timestamp;
  if (ts != null && ts !== '') {
    const d = ts instanceof Date ? ts : new Date(Number(ts));
    if (!Number.isNaN(d.getTime())) {
      lines.push(`  Время: ${d.toLocaleString('ru-RU')}`);
    }
  }
  const snaps = row.playerSnapshots || [];
  if (snaps.length > 0) {
    lines.push('  Карты и комбинации (финиш раздачи):');
    snaps.forEach((ps) => {
      const tag = ps.isHero ? 'Вы' : ps.name;
      const w = ps.isWinner ? ' [победа]' : '';
      const f = ps.isFolded ? ' [сброс]' : '';
      const cards =
        Array.isArray(ps.cardStrings) && ps.cardStrings.length > 0 ? ps.cardStrings.join(' ') : '—';
      const combo = ps.handDescription ? ` · ${ps.handDescription}` : '';
      lines.push(`    м.${ps.seatIndex + 1} ${tag}${w}${f}: ${cards}${combo}`);
    });
  }
  lines.push('  Ходы:');
  const actions = row.actions || [];
  if (actions.length > 0) {
    actions.forEach((a, j) => {
      const name = a.playerName || a.player?.name || '—';
      lines.push(`    ${j + 1}. ${name}: ${formatHistoryAction(a)}`);
    });
  } else {
    lines.push('    (не записаны)');
  }
  const deltaTitle = opts.stackDeltaTitle || 'Баланс по местам:';
  lines.push(`  ${deltaTitle}`);
  (row.deltas || []).forEach((d) => {
    const v = Number(d.delta || 0);
    const nm = d.playerName || '—';
    lines.push(`    ${nm}: ${v > 0 ? '+' : ''}${v.toLocaleString('ru-RU')} SEKA`);
  });
  lines.push('');
}

/**
 * Визуальные индикаторы типа хода для живой ленты / списков (Tailwind-классы).
 * @returns {{ abbr: string, accentBar: string, dot: string, badge: string }}
 */
export function getSekaLiveActionIndicator(actionItem) {
  const action = String(actionItem?.action || '').toUpperCase();
  const def = {
    abbr: '·',
    accentBar: 'bg-zinc-500',
    dot: 'bg-zinc-400',
    badge: 'border-white/20 bg-zinc-600/25 text-zinc-100',
  };
  const presets = {
    CHECK: {
      abbr: 'Ч',
      accentBar: 'bg-slate-400',
      dot: 'bg-slate-300',
      badge: 'border-slate-400/55 bg-slate-500/30 text-slate-50',
    },
    LOOK: {
      abbr: 'См',
      accentBar: 'bg-amber-400',
      dot: 'bg-amber-300',
      badge: 'border-amber-400/55 bg-amber-500/25 text-amber-50',
    },
    FOLD: {
      abbr: 'П',
      accentBar: 'bg-red-500',
      dot: 'bg-red-400',
      badge: 'border-red-400/55 bg-red-600/30 text-red-50',
    },
    CALL: {
      abbr: 'К',
      accentBar: 'bg-emerald-500',
      dot: 'bg-emerald-400',
      badge: 'border-emerald-400/55 bg-emerald-600/25 text-emerald-50',
    },
    RAISE: {
      abbr: 'Р',
      accentBar: 'bg-orange-500',
      dot: 'bg-orange-400',
      badge: 'border-orange-400/55 bg-orange-600/25 text-orange-50',
    },
    SHOWDOWN: {
      abbr: 'В',
      accentBar: 'bg-violet-500',
      dot: 'bg-violet-400',
      badge: 'border-violet-400/55 bg-violet-600/30 text-violet-50',
    },
    AUTO_SHOWDOWN_ALLIN_ANTE: {
      abbr: 'А',
      accentBar: 'bg-sky-500',
      dot: 'bg-sky-400',
      badge: 'border-sky-400/55 bg-sky-700/35 text-sky-50',
    },
  };
  return presets[action] || def;
}

/** Описание комбинации из записи gameHistory (hands записывает движок при вскрытии). */
function handRowPlayerKey(row) {
  const pl = row?.player;
  if (!pl || typeof pl !== 'object') return '';
  return String(pl.userId ?? pl.id ?? '');
}

export function handDescriptionFromHistoryEntry(hist, playerId) {
  const hands = Array.isArray(hist?.hands) ? hist.hands : [];
  const pid = String(playerId);
  const found = hands.find((h) => h?.player && handRowPlayerKey(h) === pid);
  return found?.hand?.description ?? null;
}

/**
 * Снимок мест на конец раздачи для аналитики: карты и комбинации из hist.players / hist.hands.
 * @param {object} hist — запись gameHistory (локальный движок или серверный снимок).
 * @param {{ heroUserId?: string|null }} [opts] — для онлайна: кого помечать как героя (локально герой всегда м.1).
 */
export function buildPlayerSnapshotsFromHistoryEntry(hist, opts = {}) {
  const players = Array.isArray(hist?.players) ? hist.players : [];
  const winnerId = hist?.winner?.userId ?? hist?.winner?.id;
  const winnerIds = new Set(
    Array.isArray(hist?.winners)
      ? hist.winners.map((w) => String(w?.userId ?? w?.id ?? ''))
      : []
  );
  if (winnerId != null) winnerIds.add(String(winnerId));

  const heroUid = opts.heroUserId != null ? String(opts.heroUserId) : '';

  const rows = players.map((p, seatIndex) => {
    const id = p?.userId ?? p?.id;
    const cardsUi = (p?.cards || []).map(sekaCardToPlaying).filter(Boolean);
    const cardStrings = [...(p?.cards || [])].filter(Boolean);
    const isWinner = winnerIds.has(String(id));
    const pid = String(id ?? '');
    return {
      key: `snap-${String(id)}-${seatIndex}`,
      seatIndex,
      name: p?.name || p?.username || `Место ${seatIndex}`,
      isFolded: Boolean(p?.isFolded),
      cardsUi,
      cardStrings,
      handDescription: handDescriptionFromHistoryEntry(hist, pid),
      isWinner,
      isHero: heroUid ? pid === heroUid : seatIndex === 0,
    };
  });
  rows.sort((a, b) => {
    if (a.isWinner !== b.isWinner) return a.isWinner ? -1 : 1;
    return a.seatIndex - b.seatIndex;
  });
  return rows;
}

/**
 * Текст для буфера: хроника (простые записи) + сырой хвост gameHistory.
 * @param {{ heroUserId?: string|null }} [exportOpts] — для строки «Ваш итог» в онлайне.
 */
export function buildSimpleSekaOutcomeExportText(chronicleTail, lastHistoryEntry, exportOpts = {}) {
  const lines = [];
  lines.push('=== Сека — снимок после раздачи ===');
  lines.push(`Время: ${new Date().toLocaleString('ru-RU')}`);
  lines.push('');
  lines.push(SEKA_HISTORY_EXPORT_HINT_LINE);
  lines.push('');
  const tail = Array.isArray(chronicleTail) ? chronicleTail.slice(-12) : [];
  if (tail.length > 0) {
    lines.push('--- Последние события ---');
    tail.forEach((entry, i) => {
      lines.push(`${i + 1}. ${entry.title}`);
      if (entry.subtitle) lines.push(`   ${entry.subtitle}`);
      (entry.steps || []).forEach((s, j) => lines.push(`   ${j + 1}) ${s}`));
      lines.push('');
    });
  }
  lines.push('--- Эта раздача (gameHistory) ---');
  if (!lastHistoryEntry) {
    lines.push('(запись отсутствует)');
    return lines.join('\n');
  }
  const pot = Number(lastHistoryEntry.pot || 0);
  lines.push(`Банк: ${pot.toLocaleString('ru-RU')} SEKA`);
  if (lastHistoryEntry.winner?.name || lastHistoryEntry.winner?.username) {
    lines.push(
      `Победитель: ${lastHistoryEntry.winner.name || lastHistoryEntry.winner.username || '—'}`
    );
  } else if (Array.isArray(lastHistoryEntry.winners) && lastHistoryEntry.winners.length) {
    lines.push(
      `Победители: ${lastHistoryEntry.winners.map((w) => w.name || w.username || w.userId || '—').join(' · ')}`
    );
  }
  const actions = Array.isArray(lastHistoryEntry.actions) ? lastHistoryEntry.actions : [];
  lines.push(`  Исход раздачи: ${describeSekaHandEndType(lastHistoryEntry, actions)}`);
  lines.push('Ходы:');
  if (actions.length === 0) {
    lines.push('  (не записаны)');
  } else {
    actions.forEach((a, j) => {
      const name = a.playerName || a.player?.name || '—';
      lines.push(`  ${j + 1}. ${name}: ${formatHistoryAction(a)}`);
    });
  }
  const heroUid = exportOpts.heroUserId != null ? String(exportOpts.heroUserId) : '';
  const deltas = Array.isArray(lastHistoryEntry.stackDeltas) ? lastHistoryEntry.stackDeltas : [];
  if (heroUid && deltas.length > 0) {
    const heroRow = deltas.find((d) => String(d.playerId || '') === heroUid);
    if (heroRow && Number.isFinite(Number(heroRow.delta))) {
      const hd = Number(heroRow.delta);
      lines.push(`  Ваш итог: ${hd > 0 ? '+' : ''}${hd.toLocaleString('ru-RU')} SEKA`);
    }
  }
  const snaps = buildPlayerSnapshotsFromHistoryEntry(lastHistoryEntry, {
    heroUserId: heroUid || null,
  });
  if (snaps.length > 0) {
    lines.push('Карты и комбинации (финиш раздачи):');
    snaps.forEach((ps) => {
      const tag = ps.isHero ? 'Вы' : ps.name;
      const w = ps.isWinner ? ' [победа]' : '';
      const f = ps.isFolded ? ' [сброс]' : '';
      const cards =
        Array.isArray(ps.cardStrings) && ps.cardStrings.length > 0 ? ps.cardStrings.join(' ') : '—';
      const combo = ps.handDescription ? ` · ${ps.handDescription}` : '';
      lines.push(`  м.${ps.seatIndex + 1} ${tag}${w}${f}: ${cards}${combo}`);
    });
  }
  if (deltas.length > 0) {
    lines.push('Стеки (дельта за раздачу):');
    deltas.forEach((d) => {
      const v = Number(d.delta || 0);
      lines.push(`  ${d.playerName}: ${v > 0 ? '+' : ''}${v.toLocaleString('ru-RU')} SEKA`);
    });
  }
  return lines.join('\n');
}
